import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import rafSchedule from 'raf-schd';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { pluginFactory } from '@atlaskit/editor-common/utils';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';
import { ACTION, reducer } from './pm-plugins/reducer';
export var pluginKey = new PluginKey('editorDisabledPlugin');
var _pluginFactory = pluginFactory(pluginKey, reducer),
  getPluginState = _pluginFactory.getPluginState;

/*
Stores the state of the editor enabled/disabled for panel and floating
toolbar to subscribe to through useSharedPluginState. Otherwise the NodeViews
won't re-render when it changes.
*/
function createPlugin(dispatch, options) {
  var scheduleEditorDisabledUpdate = rafSchedule(function (view) {
    if (getPluginState(view.state).editorDisabled !== !view.editable) {
      var disabledMeta = {
        editorDisabled: !view.editable,
        disabledByPlugin: getPluginState(view.state).disabledByPlugin
      };
      var tr = view.state.tr.setMeta(pluginKey, disabledMeta).setMeta('editorDisabledPlugin', disabledMeta);
      tr.setMeta('isLocal', true);
      view.dispatch(tr);
    }
  });
  return new SafePlugin({
    key: pluginKey,
    state: {
      init: function init() {
        var _options$initialDisab;
        return {
          editorDisabled: (_options$initialDisab = options === null || options === void 0 ? void 0 : options.initialDisabledState) !== null && _options$initialDisab !== void 0 ? _options$initialDisab : false,
          disabledByPlugin: false
        };
      },
      // @ts-ignore - Workaround for help-center local consumption

      apply: function apply(tr, pluginState) {
        var meta = tr.getMeta(pluginKey);
        if (meta) {
          if (fg('platform_editor_ai_aifc_patch_beta')) {
            if ('action' in meta) {
              return reducer(pluginState, meta);
            }
          }
          return pluginState.editorDisabled !== meta.editorDisabled ? _objectSpread(_objectSpread({}, pluginState), meta) : pluginState;
        }
        return pluginState;
      }
    },
    props: {
      // If we set to undefined it respects the previous value.
      // Prosemirror doesn't have this typed correctly for this type of behaviour
      // @ts-ignore - Workaround for help-center local consumption
      editable: fg('platform_editor_ai_aifc_patch_beta') ? function (state) {
        var _pluginKey$getState;
        var _ref = (_pluginKey$getState = pluginKey.getState(state)) !== null && _pluginKey$getState !== void 0 ? _pluginKey$getState : {
            disabledByPlugin: false
          },
          disabledByPlugin = _ref.disabledByPlugin;
        return disabledByPlugin ? false : undefined;
      } : undefined
    },
    // @ts-ignore - Workaround for help-center local consumption

    view: function view(_view) {
      // schedule on mount
      scheduleEditorDisabledUpdate(_view);
      return {
        // @ts-ignore - Workaround for help-center local consumption
        update: function update(view) {
          scheduleEditorDisabledUpdate(view);
        },
        // @ts-ignore - Workaround for help-center local consumption
        destroy: function destroy() {
          scheduleEditorDisabledUpdate.cancel();
        }
      };
    }
  });
}

/**
 * Editor disabled plugin to be added to an `EditorPresetBuilder` and used with `ComposableEditor`
 * from `@atlaskit/editor-core`.
 *
 * @param options - Plugin configuration options
 * @returns EditorDisabledPlugin
 *
 * @example
 * ```typescript
 * // Basic usage
 * .add(editorDisabledPlugin)
 *
 * // With initial disabled state
 * .add(editorDisabledPlugin, { initialDisabledState: true })
 * ```
 */
export var editorDisabledPlugin = function editorDisabledPlugin(_ref2) {
  var _ref2$config = _ref2.config,
    options = _ref2$config === void 0 ? {} : _ref2$config,
    api = _ref2.api;
  return {
    name: 'editorDisabled',
    getSharedState: function getSharedState(editorState) {
      if (!editorState) {
        return {
          editorDisabled: false
        };
      }
      var pluginState = pluginKey.getState(editorState);
      if (!pluginState) {
        return {
          editorDisabled: false
        };
      }
      if (fg('platform_editor_ai_aifc_patch_beta')) {
        return {
          editorDisabled: pluginState.disabledByPlugin || pluginState.editorDisabled
        };
      }
      return pluginState;
    },
    pmPlugins: function pmPlugins() {
      return [{
        name: 'editorDisabled',
        plugin: function plugin(_ref3) {
          var dispatch = _ref3.dispatch;
          return createPlugin(dispatch, options);
        }
      }];
    },
    commands: {
      toggleDisabled: function toggleDisabled(disabled) {
        return function (_ref4) {
          var tr = _ref4.tr;
          return fg('platform_editor_ai_aifc_patch_beta') ? tr.setMeta(pluginKey, {
            action: ACTION.TOGGLE_DISABLED,
            disabled: disabled
          }) : null;
        };
      }
    }
  };
};